// React Query key factory — single source of truth for query keys.
// Use `queryKeys.health.all` etc. for `invalidateQueries` consistency.
//
// W2 extension: diary + media + moods + pair-invite + classify_mood.

export const queryKeys = {
  health: {
    all: ["health"] as const,
    detail: () => [...queryKeys.health.all, "detail"] as const,
  },
  auth: {
    all: ["auth"] as const,
    session: () => [...queryKeys.auth.all, "session"] as const,
  },
  pairs: {
    all: ["pairs"] as const,
    detail: (pairId: string) => [...queryKeys.pairs.all, "detail", pairId] as const,
    invite: () => [...queryKeys.pairs.all, "invite"] as const,
    invitesForPair: (pairId: string) => [...queryKeys.pairs.all, "invites", pairId] as const,
    publicInvite: (code: string) => [...queryKeys.pairs.all, "public-invite", code] as const,
  },
  diary: {
    all: ["diary"] as const,
    list: (cursor: string | null, includePrivate: boolean) =>
      [...queryKeys.diary.all, "list", cursor, includePrivate] as const,
    detail: (diaryId: string) => [...queryKeys.diary.all, "detail", diaryId] as const,
    trash: () => [...queryKeys.diary.all, "trash"] as const,
  },
  media: {
    all: ["media"] as const,
  },
  album: {
    all: ["album"] as const,
    list: () => [...queryKeys.album.all, "list"] as const,
    detail: (albumId: string) => [...queryKeys.album.all, "detail", albumId] as const,
    items: (albumId: string, sort: string) =>
      [...queryKeys.album.all, "items", albumId, sort] as const,
  },
  moods: {
    all: ["moods"] as const,
  },
  anniversary: {
    all: ["anniversary"] as const,
    classify: (title: string) => [...queryKeys.anniversary.all, "classify", title] as const,
    // W3.1 extension: list + detail + timeline + today + next + nested sub-resources.
    list: () => [...queryKeys.anniversary.all, "list"] as const,
    detail: (id: string) => [...queryKeys.anniversary.all, "detail", id] as const,
    timeline: () => [...queryKeys.anniversary.all, "timeline"] as const,
    today: () => [...queryKeys.anniversary.all, "today"] as const,
    next: () => [...queryKeys.anniversary.all, "next"] as const,
    milestones: (id: string, status: string | null) =>
      [...queryKeys.anniversary.all, "milestones", id, status] as const,
    activity: (id: string) => [...queryKeys.anniversary.all, "activity", id] as const,
    diaries: (id: string) => [...queryKeys.anniversary.all, "diaries", id] as const,
  },
  // W36.0: the dashboard aggregation + the three tables behind it. `all` is
  // the invalidation root — writing a daily message changes the hero block,
  // the module row and the bell badge at once, so they must be invalidated
  // together or the page shows three versions of the same truth.
  dashboard: {
    all: ["dashboard"] as const,
    today: () => [...queryKeys.dashboard.all, "today"] as const,
    messages: () => [...queryKeys.dashboard.all, "messages"] as const,
    notifications: () => [...queryKeys.dashboard.all, "notifications"] as const,
  },
} as const;
